import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { Form } from "react-bootstrap";
import Axios from "axios";
import DateRangePicker from "react-bootstrap-daterangepicker";
import moment from "moment-timezone";
import ReactEcharts from "echarts-for-react";

import { useFormatter } from "../../util/formatter";
import { useRouter } from "./../../util/router";
import { useSite } from "./../../util/sites";
import { useUser } from "../../util/user";
import GYAxios from "../../util/GYAxios";

import LoaderGraph from "../general/LoaderGraph";
import MapkwpGraph from "./MapkwpGraph";

import "bootstrap-daterangepicker/daterangepicker.css";
import "./map.css";

function MapContent() {
	const currentSite = useSite();
	const router = useRouter();
	const [formatNumber, formatNumberStr, isEmpty] = useFormatter();

	const [selectedSite, setSelectedSite] = useState(null);
	const [duration, setDuration] = useState({
		fromDate: moment().subtract(8, "days").format("YYYY-MM-DD"),
		toDate: moment().format("YYYY-MM-DD"),
	});

	const [dataReady, setDataReady] = useState(false);
	const [hidden, setHidden] = useState(true);
	const [isLoading, setIsLoading] = useState(true);
	const [selectAll, setSelectAll] = useState(true);
	const [inverters, setInverters] = useState(null);
	const [invertersNumber, setInvertersNumber] = useState(0);
	const [selectedInv, setSelectedInv] = useState(0);
	const [mainData, setMainData] = useState(null);
	const [selectedInvData, setSelectedInvData] = useState(null);
	const [mainGraphData, setMainGraphData] = useState(null);
	const [showGraph, setShowGraph] = useState("");
	const [mainInvGraphConfig, setMainInvGraphConfig] = useState(null);
	const [singleInvGraphConfig, setSingleInvGraphConfig] = useState(null);
	const [showInverterStatus, setShowInverterStatus] = useState(false);
	const [showKWPeakGraph, setShowKWPeakGraph] = useState(false);

	const user = useUser();

	/**
	 * Format date
	 * @param {Date to be formatted} date
	 */
	const formatDate = (date) => moment(date).format("MMMM DD, YYYY");

	/**
	 * Group elements of array based on size specified
	 * @param {Array to be grouped} myArray
	 * @param {Group size} chunk_size
	 */
	const chunkArray = (myArray, chunk_size) => {
		var index = 0;
		var arrayLength = myArray.length;
		var tempArray = [];

		for (index = 0; index < arrayLength; index += chunk_size) {
			const myChunk = myArray.slice(index, index + chunk_size);
			tempArray.push(myChunk);
		}

		return tempArray;
	};

	/**
	 * Fetch inverter data based on period set
	 * @param {Selected Site} selectedSite
	 * @param {Period of data to be fetched} duration
	 */
	const getInverterData = (selectedSite, period) => {
		setIsLoading(true);

		let invertersNumber = 0;
		if (selectedSite != null) {
			invertersNumber = selectedSite.nb_of_inverter;
			setInvertersNumber(selectedSite.nb_of_inverter);
		}

		let mainGraphDataObj = {
			dateData: [],
		};

		if (invertersNumber !== 0) {
			for (let i = 1; i <= invertersNumber; i++) {
				let invPrefix = `INV ${formatNumberStr(i)}`;
				mainGraphDataObj[invPrefix] = [];
			}

			const tableName = selectedSite.table_name;
			const fromDate = period.fromDate;
			const toDate = period.toDate;

			const p10URI = `/p10/site/${tableName}/period/${fromDate}/${toDate}/order/dateData/ASC`;

			GYAxios.get(p10URI).then((res) => {
				if (res.data != null) {
					res.data.map((row) => {
						const dateData = moment(row.dateData).format("DD/MM HH:mm");
						mainGraphDataObj["dateData"].push(dateData);

						for (let i = 1; i <= invertersNumber; i++) {
							let invPrefix_obj = `INV ${formatNumberStr(i)}`;
							let invPrefix = `INV_${formatNumberStr(i)}`;
							mainGraphDataObj[invPrefix_obj].push(row[invPrefix + "_Power"]);
						}
					});
					setMainGraphData(mainGraphDataObj);
					setMainData(res.data);
				}
			});

			const inverterParam = {
				table_name: selectedSite.table_name,
			};

			const dateData = moment().format("YYYYMMDD");
			const liveURI = `/p10/single_row/site/${tableName}/date/${dateData}`;

			const reqLiveDesc = GYAxios.get(`${liveURI}/end`);
			const reqLiveAsc = GYAxios.get(`${liveURI}/start`);

			const reqInverters = GYAxios.post("/site/inverter", inverterParam);
			const reqInvStates = GYAxios.get(
				`/general_info/inverter/states/site/${tableName}`
			);

			Axios.all([reqLiveDesc, reqLiveAsc, reqInverters, reqInvStates]).then(
				Axios.spread((...responses) => {
					const res_desc = responses[0];
					const res_asc = responses[1];
					const res_inv = responses[2];
					const res_inv_states = responses[3];

					let invArr = [];
					for (let index = 1; index <= invertersNumber; index++) {
						let invObj = {};
						const invPrefix = `INV_${formatNumberStr(index)}`;
						const invName = `Inv ${formatNumberStr(index)}`;

						let instantPower = 0;
						let state = "-1";
						let control = 0;
						let totalStart = 0;
						let totalNow = 0;
						let energy = 0;

						if (res_desc.data != null && res_asc.data != null) {
							const new_arr = res_asc.data.concat(res_desc.data);

							instantPower = new_arr[1]?.[invPrefix + "_Power"];
							state = new_arr[1]?.[invPrefix + "_State"];
							control = new_arr[1]?.[invPrefix + "_Control"];
							totalStart = new_arr[0]?.[invPrefix + "_Total"];
							totalNow = new_arr[1]?.[invPrefix + "_Total"];
							energy = (totalNow && totalStart) && (totalNow - totalStart);
						}

						invObj["control"] = control || '-';
						invObj["instantPower"] = instantPower?.toFixed(2) || '-';
						invObj["state"] = state || '-';
						invObj["invPrefix"] = invPrefix || '-';
						invObj["invName"] = invName || '-';
						invObj["energy"] = energy?.toFixed(2) || '-';

						// Inverter models exist
						if (res_inv.data != null && res_inv_states.data != null) {
							const modelName = res_inv.data[index - 1].model_name;
							const brand = res_inv.data[index - 1].brand;
							const power = res_inv.data[index - 1].power;
							const kwp = res_inv.data[index - 1].kwp;

							invObj["modelName"] = brand + " " + modelName;
							invObj["power"] = power;
							invObj["kwp"] = kwp;

							let invRef = "";
							switch (modelName) {
								case "100 KTL":
								case "60 KTL":
								case "50 KTL":
								case "30 KTL":
								case "40 KTL":
								case "215 KTL": const invRefTemp = modelName.split(" ");
									invRef = invRefTemp[1];
									break;
								case "SHP 75":
								case "STP110-60":
								case "STP 50-40":
								case "SPI60K-B":
									invRef = modelName;
									break;
								default:
									const invRefTempDef = modelName.split("-");
									invRef = invRefTempDef[0];
									break;
							}

							for (let i = 0; i < res_inv_states.data.length; i++) {
								const row = res_inv_states.data[i];

								if (
									(invRef == row.model && state == row.model_state) ||
									(row.model == "ALL" && state == row.model_state)
								) {
									const state_desc = row.model_state_desc;
									const isTooLong = state_desc.length > 64;
									const shortened_desc = isTooLong
										? `${state_desc.substring(0, 64)}...`
										: state_desc;

									invObj["borderStyle"] = {
										border: row.status_colour + " 4px solid",
									};
									invObj["status_desc"] = row.status_desc;
									invObj["model_state_desc"] = state_desc;
									invObj["shorten_desc"] = shortened_desc;

									break;
								}
							}
						} else {
							invObj["borderStyle"] = {
								border: "white 4px solid",
							};
							invObj["status_desc"] = "-";
							invObj["modelName"] = "-";
							invObj["power"] = "-";
							invObj["kwp"] = "-";
							invObj["model_state_desc"] = "-";
							invObj["shorten_desc"] = "-";
						}
						invArr.push(invObj);
					}
					setInverters(invArr);
					setIsLoading(false);
				})
			);
		}
	};

	/**
	 * Format name and data of inverter
	 * @param {Prefix name of inverter} invPrefix
	 */
	const formatSingleInv = (invPrefix) => {
		let tempArr = invPrefix.split("_");
		const invName = tempArr.join(" ");
		let singleInvDataObj = {
			invName,
			dateData: [],
			irr: [],
			power: [],
			control: [],
		};

		if (mainData != null) {
			mainData.map((row) => {
				const dateData = moment(row.dateData).format("DD/MM HH:mm");

				singleInvDataObj["dateData"].push(dateData);
				singleInvDataObj["irr"].push(row.irradiance);
				singleInvDataObj["power"].push(row[`${invPrefix}_Power`]);
				singleInvDataObj["control"].push(row[`${invPrefix}_Control`]);
			});
		}

		setSelectedInvData(singleInvDataObj);
	};

	/**
	 * Reload data
	 * @param {Selected Site} selectedSite
	 * @param {Period of data to be fetched} duration
	 */
	const reloadData = (selectedSite, duration) => {
		if (selectedSite != null) {
			getInverterData(selectedSite.value, duration);
		}
	};

	/**
	 * Handle when date of picker changes
	 * @param {event} event
	 * @param {Datepicker element} picker
	 */
	const handleEvent = (event, picker) => {
		const duration_obj = {
			fromDate: picker.startDate.format("YYYY-MM-DD"),
			toDate: picker.endDate.format("YYYY-MM-DD"),
		};
		setDuration(duration_obj);
	};

	useEffect(() => {
		const roles = user.roles;
		const isGYUser = roles.includes("_GY:Base");
		const hasViewKWPRoles = roles.includes("map:MapkwpGraph:_view");

		setShowInverterStatus(isGYUser);
		setShowKWPeakGraph(hasViewKWPRoles);
	}, [user.roles]);

	useEffect(() => {
		const siteID = sessionStorage.getItem("site_id");
		if (
			currentSite.selectedSite.label === currentSite.defaultSelect.label &&
			siteID == null
		)
			router.push("/solar/overview");

		setShowGraph("fadeOut");
		setTimeout(() => {
			setHidden(true);
		}, 750);
	}, [currentSite.selectedSite]);

	useEffect(() => {
		if (
			selectedSite == null ||
			(selectedSite != null &&
				currentSite.selectedSite != null &&
				currentSite.selectedSite.label !== selectedSite.label)
		)
			setSelectedSite(currentSite.selectedSite);
	}, [currentSite]);

	useEffect(() => {
		if (duration != null && selectedSite != null) {
			setIsLoading(true);
			setDataReady(false);
			reloadData(selectedSite, duration);
		}

		const timer = setInterval(() => {
			reloadData(selectedSite, duration);
		}, 600000);
		return () => {
			clearInterval(timer);
		};
	}, [selectedSite, duration]);

	useEffect(() => {
		if (selectedInv !== 0) formatSingleInv(selectedInv);
	}, [mainData, selectedInv]);

	useEffect(() => {
		if (mainGraphData != null && !isNaN(invertersNumber)) {
			const invNo = invertersNumber;
			let series = [];
			let legends = [];
			let selected = {};

			for (let i = 1; i <= invNo; i++) {
				let invPrefix = `INV ${formatNumberStr(i)}`;

				let line = {
					name: invPrefix,
					type: "line",
					seriesLayoutBy: "row",
					lineStyle: {
						normal: {
							width: 1.2,
						},
					},
					symbolSize: 2,
					showSymbol: false,
					smooth: false,
					data: mainGraphData[invPrefix],
				};

				if (!selectAll) selected[invPrefix] = false;
				else selected[invPrefix] = true;

				series.push(line);
				legends.push(invPrefix);
			}

			let optionInv = {
				title: {
					text: "Inverters Overview",
				},
				legend: {
					selected,
					data: legends,
					itemHeight: 17,
					type: "scroll",
					orient: "vertical",
					right: 20,
					top: 10,
				},
				grid: {
					top: 60,
					width: "80%",
				},
				tooltip: {
					trigger: "axis",
					formatter: function (params) {
						let rez = "";

						// Group inverters to groups of 10
						const tempArr = chunkArray(params, 10);
						rez +=
							"<p><b>Instant Power (kW) - " + params[0].axisValue + " </b></p>";
						// Build each column with the array of groups of 10
						tempArr.map((col) => {
							rez +=
								"<div style='display:inline-block;margin: 0 5px; vertical-align:top'>";
							col.map((row) => {
								rez +=
									"<p>" +
									'<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:' +
									row.color +
									'"></span>' +
									row.seriesName +
									": " +
									row.value +
									"</p>";
							});
							rez += "</div>";
						});
						return rez;
					},
				},
				axisPointer: {
					label: {
						type: "shadow",
					},
				},
				dataZoom: [
					{
						type: "slider",
						filterMode: "filter",
						start: 90,
						end: 100,
					},
					{
						type: "inside",
						filterMode: "filter",
						realtime: true,
						start: 90,
						end: 100,
					},
				],
				dataset: {
					source: mainGraphData.dateData,
				},
				xAxis: {
					type: "category",
					axisTick: {
						alignWithLabel: true,
					},
					axisLine: {
						onZero: false,
						lineStyle: {},
					},
					axisTick: {
						show: true,
					},
					data: mainGraphData.dateData,
				},
				yAxis: {
					name: "Power",
					nameTextStyle: {
						fontWeight: "bold",
					},
					axisLine: { show: true },
					axisTick: {
						show: true,
					},
					axisLabel: {
						formatter: "{value} kW",
					},
				},
				series: series,
			};

			setMainInvGraphConfig(optionInv);
			setDataReady(true);
		}
	}, [mainGraphData, selectAll]);

	useEffect(() => {
		if (selectedInvData != null) {
			const colors = ["#f28a10", "#333", "#88b917"];
			const fieldName = selectedInvData.invName;

			const optionSelectedInv = {
				title: {
					text: "Power in kW",
					x: "left",
					align: "right",
					left: 30,
				},
				color: colors,
				tooltip: {
					trigger: "axis",
					axisPointer: {
						animation: true,
						type: "cross",
					},
				},
				legend: {
					data: [fieldName, "Irradiance", "% Control"],
					x: "center",
				},
				grid: {
					top: 70,
					bottom: 70,
					left: "5%",
					right: "15%",
				},
				xAxis: [
					{
						type: "category",
						axisTick: {
							alignWithLabel: true,
						},
						axisLine: {
							onZero: false,
							lineStyle: {
								color: colors[0],
							},
						},
						axisTick: {
							show: true,
						},
						data: selectedInvData.dateData,
					},
				],
				dataZoom: [
					{
						type: "slider",
						filterMode: "filter",
						start: 90,
						end: 100,
					},
					{
						type: "inside",
						filterMode: "filter",
						realtime: true,
						start: 90,
						end: 100,
					},
				],
				yAxis: [
					{
						type: "value",
						name: "kW",
						axisLine: {
							show: true,
						},
						axisTick: {
							show: true,
						},
					},
					{
						type: "value",
						name: "%",
						offset: 60,
						axisLine: {
							show: true,
						},
						axisTick: {
							show: true,
						},
					},
					{
						type: "value",
						name: "W/m\xB2",
						axisLine: {
							show: true,

							linestyle: {
								color: "#88b917",
								width: 4,
							},
						},
						axisTick: {
							show: true,
						},
					},
				],
				series: [
					{
						name: fieldName,
						type: "line",
						lineStyle: {
							normal: {
								width: 1.2,
							},
						},
						smooth: false,
						data: selectedInvData.power,
					},
					{
						name: "% Control",
						type: "line",
						lineStyle: {
							normal: {
								width: 1.2,
							},
						},
						smooth: false,
						yAxisIndex: 1,
						data: selectedInvData.control,
					},
					{
						name: "Irradiance",
						type: "line",
						lineStyle: {
							normal: {
								width: 1.2,
							},
						},
						yAxisIndex: 1,
						smooth: false,
						yAxisIndex: 2,
						data: selectedInvData.irr,
					},
				],
			};

			setSingleInvGraphConfig(optionSelectedInv);
		}
	}, [selectedInvData]);

	return (
		<Container fluid={true}>
			<div
				className="well"
				style={{
					display: "block",
					width: "50%",
					margin: "auto",
				}}
			>
				<h5 style={{ display: "inline-block" }}>DATE RANGE</h5>
				<div
					style={{ width: "80%", display: "inline-block", paddingLeft: "40px" }}
				>
					<DateRangePicker
						style={{}}
						onApply={handleEvent}
						dateLimit={{ months: 3 }}
						maxDate={moment()}
						ranges={{
							Today: [moment(), moment()],
							Yesterday: [
								moment().subtract(1, "days"),
								moment().subtract(1, "days"),
							],
							"Last 7 Days": [moment().subtract(6, "days"), moment()],
							"Last 30 Days": [moment().subtract(29, "days"), moment()],
							"This Month": [
								moment().startOf("month"),
								moment().endOf("month"),
							],
							"Last Month": [
								moment().subtract(1, "month").startOf("month"),
								moment().subtract(1, "month").endOf("month"),
							],
						}}
						alwaysShowCalendars
						autoApply
					>
						<Form>
							<Form.Group controlId="daterangepicker">
								<Form.Control
									type="text"
									placeholder="Select a date range"
									readOnly
									value={
										duration != null
											? `${formatDate(duration.fromDate)} - ${formatDate(
												duration.toDate
											)}`
											: "Select a date range"
									}
								/>
							</Form.Group>
						</Form>
					</DateRangePicker>
				</div>
			</div>
			<Row>
				{inverters != null && !isLoading ? (
					inverters.map((inv, key) => (
						<div
							className="col-md-24"
							onClick={() => {
								setShowGraph("fadeIn");
								setHidden(false);
								setSelectedInv(inv.invPrefix);
							}}
							key={key}
						>
							<div className="well dash-box" style={inv.borderStyle}>
								<h3 style={{ marginBottom: "-10px", marginTop: 0 }}>
									{inv.invName}
								</h3>
								<p>
									Energy: <b>{inv.energy} kWh</b>
								</p>
								<p>
									Instant Power: <b>{inv.instantPower} kW</b>
								</p>
								<p>
									State: <b>{inv.state}</b> | Description:{" "}
									<b>{inv.status_desc}</b>
								</p>
								<p>
									Control: <b>{inv.control} %</b>{" "}
									{showKWPeakGraph && (
										<>
											| kW Peak: <b>{inv.kwp} kW</b>
										</>
									)}
								</p>
								<p>
									<b>{inv.modelName}</b> | Power: <b>{inv.power} kW</b>
								</p>

								{showInverterStatus && (
									<div title={inv.model_state_desc} style={{ height: "40px" }}>
										<p>
											<b>
												<i>{inv.shorten_desc}</i>
											</b>
										</p>
									</div>
								)}
							</div>
						</div>
					))
				) : (
					<LoaderGraph style={{ height: "160px" }} />
				)}
			</Row>
			<Row>
				<Col xs={12} md={12} className="graph-container">
					{mainInvGraphConfig != null && dataReady ? (
						<>
							<ReactEcharts
								style={{ height: "600px", padding: "20px 0px 20px 0px" }}
								option={mainInvGraphConfig}
								notMerge={true}
								lazyUpdate={true}
							/>
							<Row>
								<form className="centralised" style={{ width: "20%" }}>
									<fieldset>
										<Col md={6}>
											<label className="radioLabel">
												<input
													className="radioInput"
													name="selectRadio"
													type="radio"
													value="select"
													onChange={() => {
														setSelectAll(false);
													}}
												/>
												<p className="radioText">Deselect All</p>
											</label>
										</Col>
										<Col md={6}>
											<label className="radioLabel">
												<input
													className="radioInput"
													name="selectRadio"
													type="radio"
													value="select"
													onChange={() => {
														setSelectAll(true);
													}}
													defaultChecked
												/>
												<p className="radioText">Select All</p>
											</label>
										</Col>
									</fieldset>
								</form>
							</Row>
							{showKWPeakGraph && <MapkwpGraph mainGraphData={mainGraphData} />}
						</>
					) : (
						<LoaderGraph style={{ height: "600px" }} />
					)}
				</Col>
			</Row>
			{!hidden && (
				<div className="overlay translucent">
					<div id="graphContainer" className={showGraph}>
						{singleInvGraphConfig != null && dataReady ? (
							<ReactEcharts
								style={{ height: "450px", padding: "20px 0px 20px 0px" }}
								option={singleInvGraphConfig}
								notMerge={true}
								lazyUpdate={true}
							/>
						) : (
							<LoaderGraph style={{ height: "450px" }} />
						)}
						<Button
							variant="light"
							onClick={() => {
								setShowGraph("fadeOut");
								setTimeout(() => {
									setHidden(true);
								}, 750);
								setSelectedInv(0);
							}}
							className="closeBtn"
						>
							Close
						</Button>
					</div>
				</div>
			)}
		</Container>
	);
}

export default MapContent;
